import { useCallback, useEffect, useState } from 'react'

import { fetchSession } from './api/session'
import { useLoad } from './api/useLoad'
import { Frame } from './frame/Frame'
import { useLayoutAttribute } from './frame/layout'
import { SignIn } from './SignIn'
import { Announcements } from './ui/Announce'
import { Screen } from './ui/Screen'
import { Failed, Waiting } from './ui/Status'

/**
 * Decides which of the three things a visitor sees: the way in, the refusal, or their space.
 *
 * Nothing is decided from what is stored in the browser — the service is asked on every load
 * and its answer is the truth. A page that believed its own memory would happily show a
 * signed-in frame around an expired session.
 */

/**
 * How a finished sign-in comes back to the frontend: as query parameters on the one address
 * the application has. Paths of their own would need whatever serves the built page to know
 * which of them belong to the application.
 */
interface Outcome {
  readonly refusedAccount: string | null
  readonly failed: boolean
}

function readOutcome(): Outcome {
  const parameters = new URLSearchParams(window.location.search)
  return {
    refusedAccount: parameters.get('denied'),
    failed: parameters.has('sign-in-failed'),
  }
}

/**
 * Takes the outcome out of the address once it has been read.
 *
 * A refusal that survived a reload would keep telling somebody off for something they did
 * minutes ago. Only the query is touched; the fragment is where the frame keeps its route.
 */
function forgetOutcome() {
  const url = new URL(window.location.href)
  if (url.searchParams.has('denied') || url.searchParams.has('sign-in-failed')) {
    url.searchParams.delete('denied')
    url.searchParams.delete('sign-in-failed')
    window.history.replaceState(null, '', url.toString())
  }
}

export function App() {
  useLayoutAttribute()

  const [outcome, setOutcome] = useState<Outcome>(readOutcome)
  const loadSession = useCallback((signal: AbortSignal) => fetchSession(signal), [])
  const { load, retry } = useLoad(loadSession)

  useEffect(forgetOutcome, [])

  const signedOut = useCallback(() => {
    setOutcome({ refusedAccount: null, failed: false })
    retry()
  }, [retry])

  return (
    <Announcements>
      {load.status === 'waiting' && (
        <Screen>
          <Waiting label="Opening MyLib…" />
        </Screen>
      )}

      {load.status === 'failed' && (
        <Screen>
          <Failed
            failure={load.failure}
            onRetry={retry}
            hint="Start the backend in service/, then try again."
          />
        </Screen>
      )}

      {load.status === 'ready' &&
        (load.value.signedIn ? (
          <Frame session={load.value} onSignedOut={signedOut} />
        ) : (
          <SignIn
            methods={load.value.signInMethods}
            refusedAccount={outcome.refusedAccount}
            failed={outcome.failed}
            contact={load.value.contact}
          />
        ))}
    </Announcements>
  )
}
